import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { 
  Plus, Users, Target, DollarSign, Calendar, CheckCircle,
  XCircle, Clock, Eye, Send, AlertCircle
} from 'lucide-react';

const AdminSaaSManagement = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [matches, setMatches] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [activeTab, setActiveTab] = useState('matches');

  // Form state
  const [formData, setFormData] = useState({
    matchId: '',
    startDate: '',
    endDate: '',
    totalBudget: '',
    description: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');

    if (!token || !userData) {
      navigate('/xchange/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'admin') {
      navigate('/xchange/login');
      return;
    }

    setUser(parsedUser);
    await fetchData(token);
  };

  const fetchData = async (token) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      // Fetch matches that are connected (ready for engagement)
      const matchRes = await fetch(`${backendUrl}/api/xchange/matches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const matchData = await matchRes.json();
      // Filter for connected matches only
      const connectedMatches = matchData.filter(m => m.status === 'connected');
      setMatches(connectedMatches);

      // Fetch existing engagements
      const engRes = await fetch(`${backendUrl}/api/saas/engagements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const engData = await engRes.json();
      setEngagements(engData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleCreateEngagement = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('xchange_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/saas/engagements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          matchId: formData.matchId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          totalBudget: parseFloat(formData.totalBudget),
          description: formData.description
        })
      });

      if (response.ok) {
        alert('Engagement created successfully!');
        setShowCreateModal(false);
        setFormData({
          matchId: '',
          startDate: '',
          endDate: '',
          totalBudget: '',
          description: ''
        });
        await fetchData(token);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating engagement:', error);
      alert('Failed to create engagement');
    }
  };

  const openCreateModal = (match) => {
    setSelectedMatch(match);
    setFormData({
      ...formData,
      matchId: match.id,
      description: match.projectTitle || match.intentTitle || ''
    });
    setShowCreateModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount * 100000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{borderColor: '#14B8A6'}}></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{color: '#1E3A5F'}}>
              Project Management - Create Engagements
            </h1>
            <p className="text-gray-600">Convert matches into monitored project engagements</p>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(245, 158, 11, 0.1)'}}>
                  <Clock className="w-6 h-6" style={{color: '#F59E0B'}} />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{color: '#1E3A5F'}}>{matches.length}</div>
                  <div className="text-sm text-gray-600">Ready for Engagement</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                  <CheckCircle className="w-6 h-6" style={{color: '#14B8A6'}} />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{color: '#1E3A5F'}}>{engagements.length}</div>
                  <div className="text-sm text-gray-600">Active Engagements</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(30, 58, 95, 0.1)'}}>
                  <DollarSign className="w-6 h-6" style={{color: '#1E3A5F'}} />
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{color: '#1E3A5F'}}>
                    {formatCurrency(engagements.reduce((sum, e) => sum + (e.totalBudget || 0), 0))}
                  </div>
                  <div className="text-sm text-gray-600">Total Value</div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <div className="flex gap-6">
              <button
                onClick={() => setActiveTab('matches')}
                className={`pb-3 px-1 font-medium transition-all ${
                  activeTab === 'matches'
                    ? 'border-b-2 text-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={activeTab === 'matches' ? {borderColor: '#14B8A6'} : {}}
              >
                Pending Matches ({matches.length})
              </button>
              <button
                onClick={() => setActiveTab('engagements')}
                className={`pb-3 px-1 font-medium transition-all ${
                  activeTab === 'engagements'
                    ? 'border-b-2 text-teal-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={activeTab === 'engagements' ? {borderColor: '#14B8A6'} : {}}
              >
                Active Engagements ({engagements.length})
              </button>
            </div>
          </div>

          {/* Content */}
          {activeTab === 'matches' && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-6" style={{color: '#1E3A5F'}}>
                Connected Matches Ready for Engagement
              </h3>

              {matches.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>No matches ready for engagement</p>
                  <p className="text-sm mt-2">Matches must be in "connected" status to create engagements</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {matches.map((match) => (
                    <div key={match.id} className="p-5 border-2 border-gray-200 rounded-lg hover:border-teal-200 transition-all">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg mb-2" style={{color: '#1E3A5F'}}>
                            {match.projectTitle || match.intentTitle}
                          </h4>
                          <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">Sponsor:</span>
                              <span className="font-medium">{match.corpName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">Agency:</span>
                              <span className="font-medium">{match.ngoName}</span>
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-gray-500">
                            Connected on: {formatDate(match.createdAt)}
                          </div>
                        </div>
                        <button
                          onClick={() => openCreateModal(match)}
                          className="px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg flex items-center gap-2"
                          style={{background: '#14B8A6', color: '#FFFFFF'}}
                        >
                          <Plus className="w-4 h-4" />
                          Create Engagement
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'engagements' && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-6" style={{color: '#1E3A5F'}}>
                Active Engagements
              </h3>

              {engagements.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>No active engagements</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {engagements.map((engagement) => (
                    <div 
                      key={engagement.id} 
                      className="p-5 border-2 border-gray-200 rounded-lg hover:border-teal-200 transition-all cursor-pointer"
                      onClick={() => navigate(`/saas/engagement/${engagement.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg mb-2" style={{color: '#1E3A5F'}}>
                            {engagement.projectTitle}
                          </h4>
                          <div className="grid md:grid-cols-3 gap-4 text-sm mb-2">
                            <div>
                              <span className="text-gray-600">Sponsor:</span>
                              <span className="font-medium ml-1">{engagement.companyName}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Agency:</span>
                              <span className="font-medium ml-1">{engagement.agencyName}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Budget:</span>
                              <span className="font-medium ml-1">{formatCurrency(engagement.totalBudget)}</span>
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(engagement.startDate)} - {formatDate(engagement.endDate)}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: engagement.healthStatus === 'on_track' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                              color: engagement.healthStatus === 'on_track' ? '#10B981' : '#EF4444'
                            }}
                          >
                            {engagement.healthStatus}
                          </span>
                          <button
                            className="px-4 py-2 rounded-lg font-medium transition-all border-2 flex items-center gap-2"
                            style={{borderColor: '#14B8A6', color: '#14B8A6'}}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/saas/engagement/${engagement.id}`);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Engagement Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold" style={{color: '#1E3A5F'}}>
                Create New Engagement
              </h2>
            </div>

            <form onSubmit={handleCreateEngagement} className="p-6 space-y-6">
              {/* Match Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-semibold text-gray-700 mb-2">Selected Match:</div>
                <div className="text-lg font-bold" style={{color: '#1E3A5F'}}>
                  {selectedMatch?.projectTitle || selectedMatch?.intentTitle}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {selectedMatch?.corpName} → {selectedMatch?.ngoName}
                </div>
              </div>

              {/* Form Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.startDate}
                  onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.endDate}
                  onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Budget (in Lakh INR) *
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  value={formData.totalBudget}
                  onChange={(e) => setFormData({...formData, totalBudget: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  placeholder="e.g., 50.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description *
                </label>
                <textarea
                  required
                  rows="4"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  placeholder="Describe the engagement objectives and scope..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({
                      matchId: '',
                      startDate: '',
                      endDate: '',
                      totalBudget: '',
                      description: ''
                    });
                  }}
                  className="flex-1 px-6 py-3 rounded-lg font-medium transition-all border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg"
                  style={{background: '#14B8A6', color: '#FFFFFF'}}
                >
                  Create Engagement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSaaSManagement;
